import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckIcon, ChevronDownIcon, BuildingIcon, SearchIcon } from 'lucide-react';
export interface Company {
  id: string;
  name: string;
  logo?: string;
}
interface CompanySelectorProps {
  companies: Company[];
  selectedCompanies: Company[];
  onChange: (companies: Company[]) => void;
}
export function CompanySelector({
  companies,
  selectedCompanies,
  onChange
}: CompanySelectorProps) {
  const {
    t
  } = useTranslation('analytics');
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  // Filter companies based on search query
  const filteredCompanies = companies.filter(company => company.name.toLowerCase().includes(searchQuery.toLowerCase()));
  // Select a single company (replaces any previous selection)
  const selectCompany = (company: Company) => {
    onChange([company]);
    setIsOpen(false);
    setSearchQuery('');
  };
  // Display text based on selection
  const displayText = () => {
    if (selectedCompanies.length === 0) {
      return 'Select Company';
    }
    return selectedCompanies[0].name;
  };
  return <div className="relative" ref={dropdownRef} data-id="element-1494">
      <button onClick={() => setIsOpen(!isOpen)} className="flex items-center justify-between min-w-40 max-w-64 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-1495">
        <div className="flex items-center min-w-0 flex-1" data-id="element-1496">
          {selectedCompanies.length === 1 && selectedCompanies[0].logo ? <img src={selectedCompanies[0].logo} alt={selectedCompanies[0].name} className="w-5 h-5 rounded-full mr-2 flex-shrink-0" data-id="element-1497" /> : <BuildingIcon size={16} className="mr-2 text-gray-500 flex-shrink-0" data-id="element-1498" />}
          <span className="truncate" data-id="element-1499">{displayText()}</span>
        </div>
        <ChevronDownIcon size={16} className="ml-2 flex-shrink-0" data-id="element-1500" />
      </button>
      {isOpen && <div className="absolute left-0 z-10 w-72 mt-1 bg-white border border-gray-200 rounded-md shadow-lg" data-id="element-1501">
          {/* Search input */}
          <div className="p-2 border-b border-gray-200" data-id="element-1502">
            <div className="relative" data-id="element-1503">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1504">
                <SearchIcon size={14} className="text-gray-400" data-id="element-1505" />
              </div>
              <input type="search" aria-label={t('components.CompanySelector.attrs.search_companies')} className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder={t('components.CompanySelector.attrs.search_companies')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-1506" />
            </div>
          </div>
          {/* Companies list */}
          <div className="max-h-60 overflow-y-auto" data-id="element-1507">
            {filteredCompanies.length > 0 ? filteredCompanies.map(company => {
          const isSelected = selectedCompanies.some(c => c.id === company.id);
          return <div key={company.id} onClick={() => selectCompany(company)} className={`flex items-center px-3 py-2 cursor-pointer hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`} data-id="element-1508">
                    <div className="flex items-center flex-1" data-id="element-1509">
                      {company.logo ? <img src={company.logo} alt={company.name} className="w-6 h-6 rounded-full mr-2" data-id="element-1510" /> : <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center mr-2" data-id="element-1511">
                          <span className="text-xs font-medium text-gray-600" data-id="element-1512">
                            {company.name.charAt(0)}
                          </span>
                        </div>}
                      <span className="text-sm" data-id="element-1513">{company.name}</span>
                    </div>
                    <div className="ml-2" data-id="element-1514">
                      {isSelected && <CheckIcon size={16} className="text-blue-600" data-id="element-1515" />}
                    </div>
                  </div>;
        }) : <div className="px-3 py-2 text-sm text-gray-500" data-id="element-1516">
                {t('components.CompanySelector.no_companies_found')}
              </div>}
          </div>
        </div>}
    </div>;
}